import { useState } from 'react';

import { Button, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookFormResolver } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { type ColDef } from 'ag-grid-community';
import { notificationSuccess } from 'app/core/toast';
import { useGetAllTaxes } from 'app/modules/taxes/hooks/useTaxes';
import { buttons as buttonCommon } from 'app/modules/translation';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ActionsGrid } from '../../../../../components/grid';
import { type ActionColumn } from '../../../../../components/grid/actions-column';
import GridApplication from '../../../../../components/grid/gridApplication';
import { WarningDialog } from '../../../../../core/dialog';
import { useOperationsList } from '../../../../Operations/EconomyActivity/hooks/useOperationsList';
import { type OperationTax, type OperationTaxRequest, type TaxVector } from '../../models/types';
import { baseUrl, deleteTaxOperation } from '../../services/http';
import { headers, label, messages, title } from '../../translation';

const Index = () => {
  const [search, setSearch] = useState<any>({});
  const [toggleReload, setToggleReload] = useState<any>(true);
  const { t } = useTranslation();
  const [{ data: operations, loading: loadingOperations, error: errroOperations }] =
    useOperationsList();

  const { data: taxes, loading: loadingTaxes, error: errorTaxes } = useGetAllTaxes();

  const navigate = useNavigate();

  const onSearch = (values: TaxVector) => {
    setSearch({ operationId: values?.operation?.id, taxId: values?.tax?.id });
  };

  const remove = async (row: OperationTax) => {
    const ok = await WarningDialog({
      titleModal: title('warning'),
      message: messages('removeTaxOperation'),
      okButton: buttonCommon(t, 'delete')
    });
    if (ok) {
      try {
        await deleteTaxOperation({
          taxId: row.taxId,
          operationId: row.operationId
        } as OperationTaxRequest);
        notificationSuccess(i18next.t('taxes.messages.deletedSuccessfully'));
        setToggleReload((prev: any) => !prev);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const actions: Array<ActionColumn> = [
    {
      action: ButtonAction.delete,
      variant: 'danger',

      onClick: async (row: OperationTax) => {
        await remove(row);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: headers('actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    { headerName: headers('operation'), field: 'operationName' },
    {
      headerName: headers('tax'),
      field: 'taxName'
    }
  ];

  const newTax = () => {
    navigate(`/taxes/manage/operations/create`);
  };

  if (loadingOperations || errroOperations || loadingTaxes || errorTaxes) {
    return <></>;
  }

  return (
    <>
      <PageTitle title={title('page')} />
      <HookFormResolver onSubmit={onSearch}>
        {({ reset }: HookFormMethods) => {
          return (
            <>
              {' '}
              <Row>
                <Col>
                  <Combobox label={label('tax')} name="tax" display="name" code="id" data={taxes} />
                </Col>
                <Col>
                  <Combobox
                    label={label('operation')}
                    name="operation"
                    display="name"
                    code="id"
                    data={operations}
                  />
                </Col>
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    type={'button'}
                    onClick={() => {
                      reset({ tax: null, operation: null });
                      setSearch({});
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.new} onClick={newTax} />
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>

      <hr />
      <Row>
        <Col>
          <GridApplication
            toggleReload={toggleReload}
            url={baseUrl}
            columnDefs={colDef}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
};

export default Index;
